// OAuth authorization-code flow with PKCE. Public client; secrets stay in Cognito.
const pending = new Map();
const generations = new Map();
const keyFor = config => `davax.endava.${config.clientId}`;
const decode = token => JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
const base64url = bytes => btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
const random = () => base64url(crypto.getRandomValues(new Uint8Array(32)));
function validate(config, tokens, nonce) {
  const claims = decode(tokens.id_token);
  if (claims.iss !== `https://cognito-idp.${config.region}.amazonaws.com/${config.userPoolId}` || claims.aud !== config.clientId || claims.token_use !== 'id' || claims.exp * 1000 <= Date.now() || (nonce && claims.nonce !== nonce)) throw new Error('Invalid SSO response. Please sign in again.');
  return claims;
}
async function exchange(config, parameters) {
  const response = await fetch(`${config.domain}/oauth2/token`, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: new URLSearchParams({ client_id: config.clientId, ...parameters }) });
  if (!response.ok) throw new Error('Your Endava session could not be renewed. Please sign in again.');
  return response.json();
}
export async function startEndavaSignIn(config) {
  if (!config) throw new Error('Endava SSO is not configured for this environment.');
  const state = random(), verifier = random(), nonce = random();
  const redirectUri = `${window.location.origin}/`;
  const challenge = base64url(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))));
  sessionStorage.setItem(`${keyFor(config)}.pending`, JSON.stringify({ state, verifier, nonce, redirectUri, returnPath: window.location.pathname + window.location.search + window.location.hash, createdAt: Date.now() }));
  const query = new URLSearchParams({ client_id: config.clientId, response_type: 'code', redirect_uri: redirectUri, scope: 'openid profile email', identity_provider: 'Endava', state, nonce, code_challenge_method: 'S256', code_challenge: challenge });
  window.location.assign(`${config.domain}/oauth2/authorize?${query}`);
}
export function clearEndavaSession(config) {
  if (!config) return;
  const key = keyFor(config);
  generations.set(key, (generations.get(key) || 0) + 1);
  localStorage.removeItem(keyFor(config));
  sessionStorage.removeItem(`${keyFor(config)}.pending`);
}
export function takeEndavaSignInError(config) {
  if (!config) return '';
  const key = `${keyFor(config)}.error`;
  const error = sessionStorage.getItem(key) || '';
  sessionStorage.removeItem(key);
  return error;
}
export function getEndavaSession(config) {
  if (!config) return Promise.resolve(null);
  const key = keyFor(config);
  if (pending.has(key)) return pending.get(key);
  const promise = restore(config, generations.get(key) || 0).catch(error => {
    sessionStorage.setItem(`${key}.error`, error.message);
    throw error;
  }).finally(() => pending.delete(key));
  pending.set(key, promise);
  return promise;
}
async function restore(config, generation) {
  const key = keyFor(config);
  const persist = tokens => {
    if ((generations.get(key) || 0) !== generation) throw new Error('This session was signed out. Please sign in again.');
    localStorage.setItem(key, JSON.stringify(tokens));
  };
  const query = new URLSearchParams(window.location.search);
  const context = JSON.parse(sessionStorage.getItem(`${key}.pending`) || 'null');
  if (context && (query.has('code') || query.has('error'))) {
    // Remove one-use OAuth material even when validation or exchange fails.
    sessionStorage.removeItem(`${key}.pending`);
    window.history.replaceState(null, '', context.returnPath?.startsWith('/') && !context.returnPath.startsWith('//') ? context.returnPath : '/');
    if (query.get('state') !== context.state || Date.now() - context.createdAt > 10 * 60 * 1000) throw new Error('SSO sign-in expired or the response did not match this browser. Please try again.');
    if (query.has('error')) throw new Error('Endava sign-in was cancelled or refused. Please try again.');
    const tokens = await exchange(config, { grant_type: 'authorization_code', code: query.get('code'), code_verifier: context.verifier, redirect_uri: context.redirectUri });
    validate(config, tokens, context.nonce);
    persist(tokens);
    return tokens;
  }
  if (query.has('code') || query.has('error')) {
    window.history.replaceState(null, '', window.location.pathname + window.location.hash);
    throw new Error('This SSO response has no matching browser session. Please sign in again.');
  }
  const stored = localStorage.getItem(key);
  if (!stored) return null;
  try {
    let tokens = JSON.parse(stored);
    if (decode(tokens.id_token).exp * 1000 < Date.now() + 60 * 1000) {
      if (!tokens.refresh_token) throw new Error('Missing refresh token');
      tokens = { ...tokens, ...await exchange(config, { grant_type: 'refresh_token', refresh_token: tokens.refresh_token }) };
      validate(config, tokens);
      persist(tokens);
    }
    validate(config, tokens);
    return tokens;
  } catch (error) { clearEndavaSession(config); throw error; }
}
export function endavaUser(tokens) {
  const claims = decode(tokens.id_token);
  return { username: claims['davax:user_id'] || claims.sub, email: claims.email, accessToken: tokens.access_token, hasAwsCredentials: false, signInUserSession: { getIdToken: () => ({ getJwtToken: () => tokens.id_token }) } };
}
